import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CallInsightJob {
  id: string;
  call_id: string;
  contact_id: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  created_by: string | null;
}

export function useCallInsightJobs(contactId: string) {
  return useQuery({
    queryKey: ['call-insight-jobs', contactId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('call_insights_jobs')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as CallInsightJob[];
    },
    enabled: !!contactId,
    refetchInterval: (query) => {
      // Polling a cada 3 segundos se houver jobs pendentes/processando
      const data = query.state.data as CallInsightJob[] | undefined;
      const hasActiveJobs = data?.some(job => 
        job.status === 'pending' || job.status === 'processing'
      );
      return hasActiveJobs ? 3000 : false;
    },
  });
}

export function useActiveJobForCall(callId: string | null) {
  return useQuery({
    queryKey: ['call-insight-job', callId],
    queryFn: async () => {
      if (!callId) return null;
      
      const { data, error } = await supabase
        .from('call_insights_jobs')
        .select('*')
        .eq('call_id', callId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as CallInsightJob | null;
    },
    enabled: !!callId,
    refetchInterval: (query) => {
      const data = query.state.data as CallInsightJob | null | undefined;
      const isActive = data?.status === 'pending' || data?.status === 'processing';
      return isActive ? 3000 : false;
    },
  });
}

export function useProcessingJobsForCalls(callIds: string[]) {
  return useQuery({
    queryKey: ['call-insight-jobs-batch', callIds],
    queryFn: async () => {
      if (!callIds.length) return {};
      
      const { data, error } = await supabase
        .from('call_insights_jobs')
        .select('*')
        .in('call_id', callIds)
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Criar mapa call_id -> job mais recente
      const jobMap: Record<string, CallInsightJob> = {};
      for (const job of (data || [])) {
        if (!jobMap[job.call_id]) {
          jobMap[job.call_id] = job as CallInsightJob;
        }
      }
      return jobMap;
    },
    enabled: callIds.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data as Record<string, CallInsightJob> | undefined;
      const hasActiveJobs = data && Object.values(data).some(job => 
        job.status === 'pending' || job.status === 'processing'
      );
      return hasActiveJobs ? 3000 : false;
    },
  });
}
